/**
 * Attempt to use the browser's localStorage and fallback to a local data map if access is denied
 */
import { useLocalStorage } from '@vueuse/core';
import type { MaybeComputedRef } from '@vueuse/core';
import { ref, shallowReactive, unref, watch } from 'vue';
import type { Ref } from 'vue';

let isAvailable = false;

try {
  const testKey = '__test__';
  localStorage.setItem(testKey, testKey);
  localStorage.removeItem(testKey);
  isAvailable = true;
} catch (e) {
  console.warn('localStorage not available, using fallback store');
}

const fallbackMap = shallowReactive(new Map<string, any>());

const fallbackStorage: Storage = {
  getItem: (key: string) => fallbackMap.get(key) || null,
  setItem: (key: string, value: string) => fallbackMap.set(key, value),
  removeItem: (key: string) => fallbackMap.delete(key),
  clear: () => fallbackMap.clear(),
  key: (index: number) => fallbackMap.keys[index] || null,
  get length() {
    return fallbackMap.size;
  }
};

export const safeLocalStorage = isAvailable ? localStorage : fallbackStorage;

/**
 * Returns a ref that is synced with a given key's value in the fallback storage, with an API similar to `useLocalStorage`
 *
 * @param key
 * @param defaultValue
 * @returns
 */
export const useFallbackStorage = <T>(
  key: string,
  defaultValue: MaybeComputedRef<T>
): Ref<T> => {
  const _default: T = fallbackMap.get(key) ?? unref(defaultValue);
  const valueRef = ref(_default) as Ref<T>;

  watch(valueRef, v => fallbackMap.set(key, v));

  watch(
    () => fallbackMap.get(key),
    v => {
      if (v !== undefined) valueRef.value = v;
    }
  );

  return valueRef;
};

export const useSafeLocalStorage = isAvailable
  ? (useLocalStorage as typeof useFallbackStorage)
  : useFallbackStorage;

export default safeLocalStorage;
